import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Dropdown, message, Tooltip, Alert, Table, type MenuProps } from 'antd';
import { MoreOutlined, DownOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import ExportButton from '../../../components/common/ExportButton';
import ConfirmModal from '../../../components/common/ConfirmModal';
import AddItemButton from '../../../components/common/AddItemButton';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import styled from 'styled-components';
import EditIcon from '../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../assets/icons/ic-trash.svg?react';
import LinkIcon from '../../../assets/icons/ic-link.svg?react';
import ArrowUpIcon from '../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../assets/icons/ic-arrow-down.svg?react';
import { RootState, AppDispatch } from '../../../store';
import {
  setCycles,
  setProcessesForCycle,
  clearHierarchyData,
  clearAllProcesses,
} from '../../../store/slices/hierarchySlice';
import { clearFinancialData } from '../../../store/slices/financialStatementSlice';
import { clearRiskAssessmentData } from '../../../store/slices/riskAssessmentSlice';
import { clearAllProcesses as clearCertificationProcesses } from '../../../store/slices/certificationSlice';
import {
  getCycles,
  getProcesses,
  createCycle,
  updateCycle,
  deleteCycle,
  createProcess,
  updateProcess,
  deleteProcess,
  parseBackendCycles,
  parseBackendProcesses,
  Cycle,
  Process,
  CYCLE_COLORS,
} from '../../../services/hierarchyService';
import { getControlActivities } from '../../../services/controlActivitiesService';
import { FileUpload } from '../../../services/types';
import { downloadFile } from '../../../services/fileService';
import CycleItemDrawer, { CycleFormData } from './CycleItemDrawer';
import ProcessItemDrawer, { ProcessFormData } from './ProcessItemDrawer';
import warningIcon from '../../../assets/icons/ic-warning.svg';
import { clearGapsData } from '@/store/slices/gapsSlice';
import ExportPanel, { ExportColumnConfig } from '../../../components/common/ExportPanel';

const { Title } = Typography;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const ContentWrapper = styled.div``;

const CycleSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 24px;
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CycleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CycleTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #1F2937;
  flex: 1;
`;

const CycleWeight = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #1F2937;
  flex: 0.1;
`;

const CycleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .ant-table-wrapper {
    overflow-x: auto;
  }

  .ant-table {
    font-size: 12px;
    border: none;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    color: #6B7280;
    font-size: 12px;
    padding: 8px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
    position: relative;
    overflow: visible;

    &:last-child {
      border-right: none;
    }

    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 8px;
    font-size: 12px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: middle;
    background: white;
    overflow: hidden;

    &:last-child {
      border-right: none;
    }
  }


  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }

  .ant-table-tbody > tr.ant-table-row-selected > td,
  .ant-table-tbody > tr.ant-table-row:active > td {
    background: transparent !important;
  }

  /* Summary (sub-total) row */
  .ant-table-summary > tr > td {
    background: white !important;
    border-top: 2px solid #E5E7EB !important;
    border-right: 1px solid #E5E7EB;
    padding: 10px 8px;

    &:last-child {
      border-right: none;
    }
  }

`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 500;
`;

const LoadingStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const LoadingStateText = styled.div`
  color: #6B7280;
  font-size: 15px;
  font-weight: 500;
`;

const CircleSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #E5E7EB;
  border-top-color: #2563EB;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;


const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

// Chip components
const ProcessChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
  }
`;


const ProcessOwnerChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #F8FAFC;
  color: #1F2937;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const MissingOwnerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #EF4444;
  color: #EF4444;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  height: 32px;
  white-space: nowrap;

  &:hover {
    background: #FEF2F2;
  }

  &:focus {
    outline: none;
  }
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const FileLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

// FileLink with download functionality and loading state
const FileLinkWithDownload: React.FC<{
  fileId: string;
  fileName: string;
}> = ({ fileId, fileName }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadFile(fileId, fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <FileLink onClick={handleDownload}>
      {isDownloading ? (
        <LoadingOutlined style={{ fontSize: 12 }} spin />
      ) : (
        <LinkIcon />
      )}
      {fileName}
    </FileLink>
  );
};

const ClickableNumber = styled.span`
  color: #1F2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

// Tooltip components for multiple flowcharts
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

// TooltipItem with download functionality and loading state
const TooltipItemWithDownload: React.FC<{
  fileId: string;
  fileName: string;
}> = ({ fileId, fileName }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadFile(fileId, fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <TooltipItem onClick={handleDownload}>
      {isDownloading && <LoadingOutlined style={{ marginRight: 8 }} spin />}
      {fileName}
    </TooltipItem>
  );
};


const SubTotalLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SubTotalValue = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  text-align: center;
`;

const PageEmptyStateWrapper = styled.div`
  text-align: center;
  padding: 100px 20px;
  min-height: 400px;
`;

const PageEmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 500;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryLabel = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SummaryValue = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #2563EB;
`;

const ErrorBanner = styled(Alert)`
  margin-top: 16px;
  height: 52px;
  display: flex;
  align-items: center;
  background: #FEF9C3 !important;
  border: 1px solid #FEF08A !important;
  border-radius: 6px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);

  .ant-alert-message {
    font-family: Inter;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0.005em;
    color: #854D0E;
  }

  .ant-alert-icon {
    color: #854D0E;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;


// Helper function to fetch and update processes for a cycle
const fetchAndUpdateProcesses = async (
  cycleId: string,
  dispatch: AppDispatch,
): Promise<Process[]> => {
  const request = {
    requiresCounts: true,
    skip: 0,
    take: 1000,
    sorted: [{ name: 'ProcessCode', direction: 'ascending' as const }],
  };

  const response = await getProcesses(cycleId, request);
  const parsedProcesses = parseBackendProcesses(response.result, cycleId);
  dispatch(setProcessesForCycle({
    cycleId,
    processes: parsedProcesses,
  }));

  return parsedProcesses;
};

// CycleItem Component
interface CycleItemProps {
  index: number;
  cycle: Cycle;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onEditCycle: (cycle: Cycle) => void;
  onDeleteCycle: (cycle: Cycle) => void;
  onEditProcess: (process: Process, shouldAutoSearchOwner?: boolean) => void;
  onDeleteProcess: (process: Process) => void;
  onAddProcess: (cycleId: string) => void;
  onAddProcessAbove: (process: Process) => void;
  onAddProcessBelow: (process: Process) => void;
  onRiskRankingClick: (process: Process) => void;
  onTotalControlsClick: (process: Process) => void;
  onKeyControlsClick: (process: Process) => void;
  onNavigateToGaps: (processId?: string, cycleId?: string, riskRating?: string) => void;
  /** Cumulative sum of control weights per process (from single report-wide fetch) */
  controlWeightByProcessId: Record<string, number>;
  onControlWeightClick: (process: Process) => void;
  cycleRef?: (ref: HTMLDivElement | null) => void;
}

const CycleItem: React.FC<CycleItemProps> = ({
  index,
  cycle,
  isCollapsed,
  onToggleCollapse,
  onEditCycle,
  onDeleteCycle,
  onEditProcess,
  onDeleteProcess,
  onAddProcess,
  onAddProcessAbove,
  onAddProcessBelow,
  onRiskRankingClick,
  onTotalControlsClick,
  onKeyControlsClick,
  onNavigateToGaps,
  controlWeightByProcessId,
  onControlWeightClick,
  cycleRef,
}) => {
  // Use Redux directly
  const dispatch = useDispatch<AppDispatch>();
  const { processesByCycleId, fetchedCycleIds } = useSelector((state: RootState) => state.hierarchy);
  const processes = processesByCycleId[cycle.id] || [];
  const hasCachedProcesses = fetchedCycleIds.includes(cycle.id);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const hasAttemptedFetch = useRef(false);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Fetch processes when component mounts (Offline-first with smart caching)
  useEffect(() => {
    const fetchProcesses = async () => {
      // Skip if already attempted fetch in this mount
      if (hasAttemptedFetch.current) {
        return;
      }

      // Skip if already have cached data (fetched in previous mount or session)
      if (hasCachedProcesses) {
        // console.log(`✅ Using cached processes for cycle ${cycle.cycleCode}`);
        return;
      }

      // Mark as attempted
      hasAttemptedFetch.current = true;

      // console.log(`🔄 Fetching processes for cycle ${cycle.cycleCode}...`);
      setIsLoadingProcesses(true);

      try {
        await fetchAndUpdateProcesses(cycle.id, dispatch);
        // console.log(`✅ Fetched ${parsedProcesses.length} processes for cycle ${cycle.cycleCode}`);
      } catch (error: any) {
        //console.error(`❌ Failed to fetch processes for cycle ${cycle.id}:`, error);
        message.error(`Failed to load processes for cycle ${cycle.cycleCode}`);
      } finally {
        setIsLoadingProcesses(false);
      }
    };

    fetchProcesses();
  }, [cycle.id, cycle.cycleCode, hasCachedProcesses, dispatch]);

  const hasProcesses = processes.length > 0;

  // Action template for process rows
  const actionTemplate = (props: any) => {
    const getMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onAddProcessAbove(props),
      },
      {
        key: 'add-below',
        label: (
          <MenuItemContent>
            <ArrowDownIcon />
            <MenuItemText>Add row below</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onAddProcessBelow(props),
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onEditProcess(props),
      },
      {
        key: 'delete',
        label: (
          <MenuItemContent>
            <TrashIcon />
            <MenuItemText $danger>Delete</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onDeleteProcess(props),
      },
    ];

    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div onClick={handleActionButtonClick}>
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton >
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      </div>
    );
  };

  // Column templates
  const processNameTemplate = (props: any) => {
    const fullText = `${props.processCode} ${props.processName}`;

    return (
      <Tooltip
        title={
          <div>
            <TooltipTitle>Process</TooltipTitle>
            <TooltipList>
              <TooltipItem onClick={() => onEditProcess(props)}>
                {fullText}
              </TooltipItem>
            </TooltipList>
          </div>
        }
        placement="topLeft"
        overlayStyle={{ maxWidth: 320 }}
        overlayInnerStyle={{
          background: '#fff',
          color: '#1F2937',
          borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          padding: '12px 14px',
        }}
      >
        <ProcessChip onClick={() => onEditProcess(props)}>{fullText}</ProcessChip>
      </Tooltip>
    );
  };

  const processOwnerTemplate = (props: any) => {
    // Show missing owner alert if processOwner is empty
    if (!props.processOwner || props.processOwner.trim() === '') {
      return (
        <MissingOwnerButton onClick={() => onEditProcess(props, true)}>
          Missing Owner<span style={{ fontSize: '20px' }}>›</span>
        </MissingOwnerButton>
      );
    }

    return (
      <ProcessOwnerChip onClick={() => onEditProcess(props, true)}>
        {props.processOwner}
      </ProcessOwnerChip>
    );
  };

  const riskRankingTemplate = (props: any) => {
    return (
      <RiskChip $risk={props.riskRanking} onClick={() => onRiskRankingClick(props)}>
        {props.riskRanking}
      </RiskChip>
    );
  };

  const totalControlsTemplate = (props: any) => {
    return (
      <ClickableNumber onClick={() => onTotalControlsClick(props)}>
        {props.totalControls}
      </ClickableNumber>
    );
  };

  const keyControlsTemplate = (props: any) => {
    return (
      <ClickableNumber onClick={() => onKeyControlsClick(props)}>
        {props.keyControls}
      </ClickableNumber>
    );
  };

  const flowchartsTemplate = (props: any) => {
    if (!props.flowcharts || props.flowcharts.length === 0) {
      return null;
    }

    const first = props.flowcharts[0] as FileUpload;
    const extra = props.flowcharts.length - 1;

    const tooltipContent = (
      <div>
        <TooltipTitle>Flowcharts</TooltipTitle>
        <TooltipList>
          {props.flowcharts.map((file: FileUpload) => (
            <TooltipItemWithDownload key={file.id} fileId={file.id} fileName={file.name} />
          ))}
        </TooltipList>
      </div>
    );

    const flowchartsContent = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
        <FileLinkWithDownload fileId={first.id} fileName={first.name} />
        {extra > 0 && (
          <span style={{
            background: '#E5E7EB',
            color: '#6B7280',
            borderRadius: 50,
            padding: '2px 7px',
            fontSize: 11,
            fontWeight: 500,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}>
            +{extra}
          </span>
        )}
      </div>
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '400px' }}
        overlayInnerStyle={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        color="#FFFFFF"
      >
        {flowchartsContent}
      </Tooltip>
    );
  };

  const testSheetsTemplate = (props: any) => {
    if (!props.testSheet) {
      return null;
    }

    const tooltipContent = (
      <div>
        <TooltipTitle>Test Sheets</TooltipTitle>
        <TooltipList>
          <TooltipItemWithDownload
            fileId={props.testSheet.id}
            fileName={props.testSheet.name}
          />
        </TooltipList>
      </div>
    );

    const testSheetContent = (
      <div>
        <FileLinkWithDownload
          fileId={props.testSheet.id}
          fileName={props.testSheet.name}
        />
      </div>
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '400px' }}
        overlayInnerStyle={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        color="#FFFFFF"
      >
        {testSheetContent}
      </Tooltip>
    );
  };

  const weightTemplate = (props: any) => {
    if (props.inScopeControlCount === 0) {
      return <span style={{ color: '#1F2937' }}>N/A</span>;
    }
    // Truncate to 2 decimal places
    const value = typeof props.processWeight === 'number' ? props.processWeight.toFixed(0) : 0;
    return `${value}%`;
  };

  const controlWeightTemplate = (props: any) => {
    if (props.inScopeControlCount === 0) {
      return <span style={{ color: '#1F2937' }}>N/A</span>;
    }
    const sum = controlWeightByProcessId[props.id] ?? 0;
    const ok = Math.abs(sum - 100) < 0.001;
    return (
      <ClickableNumber
        onClick={() => onControlWeightClick(props)}
        style={{ color: ok ? '#1F2937' : '#DC2626' }}
      >
        {sum.toFixed(0)}%
      </ClickableNumber>
    );
  };

  const testingStatusTemplate = (props: any) => {
    if (props.inScopeControlCount === 0) {
      return <span style={{ color: '#1F2937' }}>N/A</span>;
    }
    // Truncate to 2 decimal places
    const value = typeof props.testingStatus === 'number' ? props.testingStatus : 0;
    return `${value.toFixed(0)}%`;
  };

  const gapsTemplate = (props: any) => {
    // Handle empty gaps - use totalGaps from service directly
    if (!props.totalGaps || props.totalGaps === 0) {
      return <span style={{ color: '#6B7280', fontSize: '14px' }}>-</span>;
    }

    // Use totalGaps directly from the service
    const totalGaps = props.totalGaps;

    // Build tooltip content showing breakdown by severity
    const tooltipContent = (
      <div>
        <TooltipTitle>GAPs Breakdown</TooltipTitle>
        <TooltipList>
          {props.gaps.map((gap: any) => (
            <TooltipItem
              key={gap.severity}
              onClick={(e) => {
                e.stopPropagation();
                onNavigateToGaps(props.id, undefined, gap.severity);
              }}
            >
              {gap.severity}: {gap.count}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    );

    // Show only total count in a single grey chip to conserve vertical space
    const gapsContent = (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span
          onClick={() => onNavigateToGaps(props.id)}
          style={{
            backgroundColor: '#F3F4F6',
            color: '#6B7280',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#E5E7EB';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#F3F4F6';
          }}
        >
          Total - {totalGaps}
        </span>
      </div>
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '300px' }}
        overlayInnerStyle={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        color="#FFFFFF"
      >
        {gapsContent}
      </Tooltip>
    );
  };

  // Calculate Sub Total
  const calculateSubTotal = (processes: Process[]) => {
    if (!processes || processes.length === 0) {
      return {
        totalControls: 0,
        keyControls: 0,
        avgTestingStatus: 0,
        totalWeight: 0,
        totalControlWeight: 0,
        gapMap: new Map(),
        noneHaveInScopeControls: true,
      };
    }

    const noneHaveInScopeControls = processes.every(p => !p.inScopeControlCount);

    const totalControls = processes.reduce((sum, p) => sum + p.totalControls, 0);
    const keyControls = processes.reduce((sum, p) => sum + p.keyControls, 0);
    const avgTestingStatus = processes.length > 0 && processes.filter(p => p.inScopeControlCount != 0).length != 0
      ? Number(((processes.filter(p => p.inScopeControlCount != 0).reduce((sum, p) => sum + p.testingStatus * p.processWeight, 0)) / (processes.filter(p => p.inScopeControlCount != 0).reduce((sum, p) => sum + p.processWeight, 0))).toFixed(0))
      : 0;

    const totalWeight = processes.length > 0 && processes.filter(p => p.inScopeControlCount != 0).length != 0
      ? Number(processes.filter(p => p.inScopeControlCount != 0).reduce((sum, p) => sum + p.processWeight, 0))
      : 0;

    const totalControlWeight = processes.filter(p => p.inScopeControlCount != 0).reduce(
      (sum, p) => sum + (controlWeightByProcessId[p.id] ?? 0),
      0,
    );

    const gapMap = new Map<string, number>();
    processes.forEach(p => {
      p.gaps.forEach(gap => {
        gapMap.set(gap.severity, (gapMap.get(gap.severity) || 0) + gap.count);
      });
    });

    return { totalControls, keyControls, avgTestingStatus, gapMap, totalWeight, totalControlWeight, noneHaveInScopeControls };
  };

  // Cycle action menu
  const getCycleMenuItems = (): MenuProps['items'] => [
    {
      key: 'edit',
      label: (
        <MenuItemContent>
          <EditIcon />
          <MenuItemText>Edit</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => onEditCycle(cycle),
    },
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <TrashIcon />
          <MenuItemText $danger>Delete</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => onDeleteCycle(cycle),
    },
  ];

  let columns: any[] = [
    {
      title: 'Process',
      key: 'processCode',
      width: 340,
      render: (_: any, record: Process) => processNameTemplate(record),
    },
    {
      title: 'Process Owner',
      key: 'processOwner',
      width: 140,
      ellipsis: true,
      render: (_: any, record: Process) => processOwnerTemplate(record),
    },
    {
      title: 'Risk Ranking',
      key: 'riskRanking',
      width: 120,
      ellipsis: true,
      render: (_: any, record: Process) => riskRankingTemplate(record),
    },
    {
      title: '# Controls',
      key: 'totalControls',
      width: 100,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => totalControlsTemplate(record),
    },
    {
      title: '# Key Controls',
      key: 'keyControls',
      width: 110,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => keyControlsTemplate(record),
    },
    {
      title: 'Flowcharts',
      key: 'flowcharts',
      width: 130,
      ellipsis: true,
      render: (_: any, record: Process) => flowchartsTemplate(record),
    },
    {
      title: 'Test Sheets',
      key: 'testSheet',
      width: 120,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => testSheetsTemplate(record),
    },
    {
      title: 'Process Weight',
      key: 'processWeight',
      width: 120,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => weightTemplate(record),
    },
    {
      title: 'Control Weight',
      key: 'cumulativeControlWeight',
      width: 120,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => controlWeightTemplate(record),
    },
    {
      title: 'Testing Status',
      key: 'testingStatus',
      width: 110,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => testingStatusTemplate(record),
    },
    {
      title: 'Gaps',
      key: 'gaps',
      width: 80,
      align: 'center' as const,
      ellipsis: true,
      render: (_: any, record: Process) => gapsTemplate(record),
    },
  ];

  if (isAdmin) {
    columns.push({
      title: 'Action',
      key: 'action',
      width: 70,
      align: 'center' as const,
      render: (_: any, record: Process) => actionTemplate(record),
    });
  }

  const bgColor = CYCLE_COLORS[index % CYCLE_COLORS.length];
  return (
    <CycleSection $bgColor={bgColor} ref={cycleRef}>
      <CycleHeader onClick={onToggleCollapse}>
        <CollapseIcon>
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </CollapseIcon>
        <CycleTitle>{cycle.cycleCode} {cycle.cycleName}</CycleTitle>
        <CycleWeight>Weight: {cycle.cycleWeight.toFixed(0)}%</CycleWeight>
        {isAdmin && (
          <CycleActions onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: getCycleMenuItems() }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionButton>
                <MoreOutlined />
              </ActionButton>
            </Dropdown>
          </CycleActions>
        )}
      </CycleHeader>

      {!isCollapsed && (
        <>
          {isLoadingProcesses ? (
            <LoadingStateWrapper>
              <CircleSpinner />
              <LoadingStateText>Loading processes...</LoadingStateText>
            </LoadingStateWrapper>
          ) : hasProcesses ? (
            <TableContainer>
              <Table<Process>
                rowKey="id"
                dataSource={processes}
                columns={columns}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 1420 }}
                summary={() => {
                  const subTotal = calculateSubTotal(processes);
                  const noScope = subTotal.noneHaveInScopeControls;
                  const gapEntries = Array.from(subTotal.gapMap.entries());
                  const totalGaps = gapEntries.reduce((sum, [, count]) => sum + count, 0);
                  return (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <SubTotalLabel>Sub Total</SubTotalLabel>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} align="center">
                          <ClickableNumber onClick={() => onTotalControlsClick({ cycleId: cycle.id } as any)}>
                            {subTotal.totalControls}
                          </ClickableNumber>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="center">
                          <ClickableNumber onClick={() => onKeyControlsClick({ cycleId: cycle.id } as any)}>
                            {subTotal.keyControls}
                          </ClickableNumber>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} />
                        <Table.Summary.Cell index={6} />
                        <Table.Summary.Cell index={7} align="center">
                          <SubTotalValue
                            style={{
                              color:
                                noScope || subTotal.totalWeight === 0 || subTotal.totalWeight === 100
                                  ? 'black'
                                  : 'red',
                            }}
                          >
                            {!isAdmin ? ''
                              : noScope
                                ? 'N/A'
                                : subTotal.totalWeight !== 0
                                  ? `${subTotal.totalWeight.toFixed(0)}%`
                                  : 'N/A'}
                          </SubTotalValue>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} align="center">
                          {/* Control Weight — hidden for now, dont touch it */}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={9} align="center">
                          <SubTotalValue>
                            {noScope || isNaN(subTotal.avgTestingStatus)
                              ? 'N/A'
                              : `${subTotal.avgTestingStatus.toFixed(0)}%`}
                          </SubTotalValue>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10} align="center">
                          {gapEntries.length === 0 ? (
                            <span style={{ color: '#6B7280', fontSize: '14px' }}>-</span>
                          ) : (
                            <Tooltip
                              title={
                                <div>
                                  <TooltipTitle>GAPs Breakdown ({cycle.cycleCode})</TooltipTitle>
                                  <TooltipList>
                                    {gapEntries.map(([severity, count]) => (
                                      <TooltipItem
                                        key={severity}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onNavigateToGaps(undefined, cycle.id, severity);
                                        }}
                                      >
                                        {severity}: {count}
                                      </TooltipItem>
                                    ))}
                                  </TooltipList>
                                </div>
                              }
                              placement="top"
                              overlayStyle={{ maxWidth: '300px' }}
                              overlayInnerStyle={{
                                backgroundColor: '#FFFFFF',
                                padding: '16px',
                                borderRadius: '12px',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                              }}
                              color="#FFFFFF"
                            >
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <span
                                  onClick={() => onNavigateToGaps(undefined, cycle.id)}
                                  style={{
                                    backgroundColor: '#F3F4F6',
                                    color: '#6B7280',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                                >
                                  Total - {totalGaps}
                                </span>
                              </div>
                            </Tooltip>
                          )}
                        </Table.Summary.Cell>
                        {isAdmin && <Table.Summary.Cell index={11} />}
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </TableContainer>
          ) : (
            <EmptyStateWrapper>
              <EmptyStateText>There are no processes to display yet.</EmptyStateText>
              <AddItemButton onClick={() => onAddProcess(cycle.id)} text="Add Process" disabled={!isAdmin} />
            </EmptyStateWrapper>
          )}
        </>
      )}
    </CycleSection>
  );
};

const ProcessHierarchyPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const state = location.state as { selectedCycleId?: string; selectedProcessId?: string } | null;

  // Get selectedReport from Redux to access annualReportId
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const selectedReportId = selectedReport?.annualReportId;

  // Get hierarchy data from Redux
  const {
    cycles,
    lastFetchedReportId,
    processesByCycleId,
  } = useSelector((state: RootState) => state.hierarchy);

  // Local loading states
  const [isLoadingCycles, setIsLoadingCycles] = useState(false);
  const [collapsedCycles, setCollapsedCycles] = useState<Set<string>>(new Set());

  // Cycle Drawer states
  const [isCycleDrawerOpen, setIsCycleDrawerOpen] = useState(false);
  const [cycleDrawerMode, setCycleDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedCycleForEdit, setSelectedCycleForEdit] = useState<Cycle | null>(null);

  // Process Drawer states
  const [isProcessDrawerOpen, setIsProcessDrawerOpen] = useState(false);
  const [processDrawerMode, setProcessDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedProcessForEdit, setSelectedProcessForEdit] = useState<Process | null>(null);
  const [currentCycleId, setCurrentCycleId] = useState<string>('');
  const [insertProcessAtIndex, setInsertProcessAtIndex] = useState<number | null>(null);
  const [nextProcessCode, setNextProcessCode] = useState<string>('');
  const [autoEnterSearchMode, setAutoEnterSearchMode] = useState(false);

  // Delete confirmation modal state
  const [showDeleteCycleModal, setShowDeleteCycleModal] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState<Cycle | null>(null);
  const [isDeletingCycle, setIsDeletingCycle] = useState(false);

  const [showDeleteProcessModal, setShowDeleteProcessModal] = useState(false);
  const [processToDelete, setProcessToDelete] = useState<Process | null>(null);
  const [isDeletingProcess, setIsDeletingProcess] = useState(false);

  // Export panel state
  const [exportPanelOpen, setExportPanelOpen] = useState(false);

  /** Single report-wide fetch: sum of control weights per process for Control Weight column */
  const [controlWeightByProcessId, setControlWeightByProcessId] = useState<Record<string, number>>({});

  // Tracks which report was last fetched to detect fiscal year changes
  const lastFetchedForReport = useRef<string | null>(null);

  // Ref to scroll to newly created cycle
  const cycleRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Ref to scroll to bottom of page
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // Ref to track if we've already processed navigation state (prevent double scroll)
  const hasProcessedNavigationRef = useRef(false);

  const fetchCyclesData = async (silent: boolean = false) => {
    if (!selectedReportId) {
      return;
    }
    lastFetchedForReport.current = selectedReportId;
    if (!silent) {
      setIsLoadingCycles(true);
    }

    const request = {
      requiresCounts: true,
      skip: 0,
      take: 1000,
      sorted: [{ name: 'CycleCode', direction: 'ascending' as const }],
    };
    try {
      const response = await getCycles(selectedReportId, request);
      const parsedCycles = parseBackendCycles(response.result);
      dispatch(setCycles({
        cycles: parsedCycles,
        reportId: selectedReportId,
      }));
      // console.log(`✅ Fetched ${parsedCycles.length} cycles`);
    } catch (error: any) {
      console.error('❌ Failed to fetch cycles:', error);
      if (!silent) {
        message.error('Failed to load cycles');
      }
      // Reset ref on error to allow retry
      lastFetchedForReport.current = null;
    } finally {
      setIsLoadingCycles(false);
    }
  };

  // Fetch cycles when selectedReport changes (Offline-first approach with smart caching)
  useEffect(() => {
    if (!selectedReportId) {
      dispatch(clearHierarchyData());
      lastFetchedForReport.current = null;
      return;
    }

    const alreadyFetchedForThisReport = lastFetchedForReport.current === selectedReportId;
    const hasCachedData = lastFetchedReportId === selectedReportId;

    if (alreadyFetchedForThisReport && hasCachedData) {
      return;
    }

    fetchCyclesData();
  }, [selectedReportId, lastFetchedReportId, dispatch]);

  // One API call for all controls in the report — sum control weights per process (EX3 column)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedReportId) {
        setControlWeightByProcessId({});
        return;
      }
      try {
        const { data } = await getControlActivities({
          annualReportId: selectedReportId,
          skip: 0,
          take: 10000,
        });
        if (cancelled) {
          return;
        }
        const map: Record<string, number> = {};
        for (const c of data) {
          const pid = c.process.id;
          const w = c.controlWeight ?? 0;
          map[pid] = (map[pid] ?? 0) + w;
        }
        setControlWeightByProcessId(map);
      } catch {
        message.error('Failed to load control weights');
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedReportId, location.key]);

  // Handle navigation from Financial Statements page
  useEffect(() => {
    if (!state) {
      return;
    }
    // Skip if already processed this navigation state
    if (hasProcessedNavigationRef.current) {
      return;
    }
    // Wait for data to be loaded before scrolling
    if (cycles.length === 0) {
      return;
    }
    const scrollToCycle = (cycleId: string) => {
      // Expand the cycle if it's collapsed
      setCollapsedCycles(prev => {
        const newSet = new Set(prev);
        newSet.delete(cycleId);
        return newSet;
      });
      // Use window.scrollTo like in add cycle (more reliable)
      setTimeout(() => {
        const cycleRef = cycleRefs.current[cycleId];
        if (cycleRef) {
          // Get element position
          const elementTop = cycleRef.getBoundingClientRect().top;
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = elementTop + scrollTop - 100; // 100px offset for header
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });
        }
      }, 500);
    };

    if (state.selectedCycleId) {
      scrollToCycle(state.selectedCycleId);
      // Mark as processed
      hasProcessedNavigationRef.current = true;
    } else if (state.selectedProcessId) {
      // Find which cycle contains this process
      let foundCycleId: string | null = null;
      for (const cycleId in processesByCycleId) {
        const processes = processesByCycleId[cycleId];
        if (processes.some(p => p.id === state.selectedProcessId)) {
          foundCycleId = cycleId;
          break;
        }
      }
      if (foundCycleId) {
        scrollToCycle(foundCycleId);
        // Mark as processed
        hasProcessedNavigationRef.current = true;
      }
    }
  }, [state, cycles, processesByCycleId]);

  const toggleCycleCollapse = (cycleId: string) => {
    setCollapsedCycles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cycleId)) {
        newSet.delete(cycleId);
      } else {
        newSet.add(cycleId);
      }
      return newSet;
    });
  };

  // Generate next alphabetic cycle code (A, B, C, ... Z)
  const getNextCycleCode = (): string => {
    const nextIndex = cycles.length;
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return alphabet[nextIndex];
  };

  // Generate next process code for a given cycle (e.g., A01, A02, A03...)
  const getNextProcessCode = (cycleId: string, insertProcessAtIndex: number | null): string => {
    const cycle = cycles.find(c => c.id === cycleId);
    if (!cycle) {
      return '';
    }
    const processes = processesByCycleId[cycleId] || [];
    const cyclePrefix = cycle.cycleCode.charAt(0); // Get first letter (A, B, C...)
    // Calculate index based on insert position
    let codeNumber: number;
    if (insertProcessAtIndex !== null) {
      // Insert at specific position (Add Above/Below)
      codeNumber = insertProcessAtIndex + 1;
    } else {
      // Add at end
      codeNumber = processes.length + 1;
    }
    // Format: A01, A02, ... A09, A10, A11...
    return `${cyclePrefix}${codeNumber.toString().padStart(2, '0')}`;
  };

  const handleAddCycle = () => {
    setCycleDrawerMode('add');
    setSelectedCycleForEdit(null);
    setIsCycleDrawerOpen(true);
  };

  const handleEditCycle = (cycle: Cycle) => {
    setCycleDrawerMode('edit');
    setSelectedCycleForEdit(cycle);
    setIsCycleDrawerOpen(true);
  };

  const handleDeleteCycle = (cycle: Cycle) => {
    setCycleToDelete(cycle);
    setShowDeleteCycleModal(true);
  };

  const handleDeleteCycleConfirm = async () => {
    if (!cycleToDelete || !selectedReportId) {
      message.error('Cannot delete cycle');
      return;
    }

    setIsDeletingCycle(true);
    try {
      // Call API to delete cycle
      await deleteCycle(cycleToDelete.id);
      message.success('Cycle deleted successfully');
      // Close modal
      setShowDeleteCycleModal(false);
      setCycleToDelete(null);
      // Clear all processes cache because process codes will be re-indexed
      // (e.g., E01 becomes D01 when cycle D is deleted)
      dispatch(clearAllProcesses());
      // Refresh cycles list (without showing loading)
      await fetchCyclesData();
      // Invalidate financial statement cache so it refetches on next visit
      // (Cycles linked to financial statement items may have been removed)
      dispatch(clearFinancialData());
      // Clear risk assessment cache (cycles contain processes which map 1:1 to RiskAssessments)
      dispatch(clearRiskAssessmentData());
      // Clear certification cache (process owner assignments may have changed)
      dispatch(clearCertificationProcesses());
      // Clear gaps cache (cycles contain processes which map 1:1 to Gaps)
      dispatch(clearGapsData());
    } catch (error) {
      //console.error('Failed to delete cycle:', error);
      message.error('Failed to delete cycle');
    } finally {
      setIsDeletingCycle(false);
    }
  };

  const handleDeleteCycleCancel = () => {
    setShowDeleteCycleModal(false);
    setCycleToDelete(null);
  };

  const handleCycleDrawerClose = () => {
    setIsCycleDrawerOpen(false);
    setSelectedCycleForEdit(null);
  };

  const handleCycleSubmit = async (formData: CycleFormData) => {
    if (!selectedReportId) {
      message.error('No annual report selected');
      throw new Error('No annual report selected');
    }
    try {
      if (cycleDrawerMode === 'add') {
        // Create new cycle via API
        // console.log('Creating new cycle:', formData);
        await createCycle({
          annualReportId: selectedReportId,
          cycleName: formData.cycleName,
          cycleWeight: formData.cycleWeight,
        });
      } else {
        // Edit existing cycle via API
        if (!selectedCycleForEdit?.id) {
          message.error('No cycle selected for editing');
          throw new Error('No cycle selected for editing');
        }
        await updateCycle({
          annualReportId: selectedReportId,
          cycleId: selectedCycleForEdit.id,
          cycleName: formData.cycleName,
          cycleWeight: formData.cycleWeight,
        });

        // Clear financial data cache when cycle is edited
        // (Cycle name may have changed, affecting linked financial statements)
        dispatch(clearFinancialData());
        dispatch(clearRiskAssessmentData());
        // Clear gaps cache (cycles contain processes which map 1:1 to Gaps)
        dispatch(clearGapsData());
      }

      // Refresh cycles list (without showing loading)
      await fetchCyclesData(true);

      if (cycleDrawerMode === 'add') {
        setTimeout(() => {
          if (contentWrapperRef.current) {
            const scrollHeight = contentWrapperRef.current.scrollHeight;
            window.scrollTo({
              top: scrollHeight,
              behavior: 'smooth',
            });
          } else {
            // Fallback: scroll to bottom of window
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: 'smooth',
            });
          }
        }, 500);
      }
      message.success(`Cycle ${cycleDrawerMode === 'add' ? 'created' : 'updated'} successfully`);
    } catch (error) {
      const defaultErrorMessage = `Failed to ${cycleDrawerMode === 'add' ? 'create' : 'update'} cycle`;
      message.error(error instanceof Error ? error.message : defaultErrorMessage);
      throw error;
    }
  };

  const handleAddProcess = (cycleId: string) => {
    setCurrentCycleId(cycleId);
    setProcessDrawerMode('add');
    setSelectedProcessForEdit(null);
    setInsertProcessAtIndex(null); // Reset insert position - add at end
    setNextProcessCode(getNextProcessCode(cycleId, null));
    setIsProcessDrawerOpen(true);
  };

  const handleAddProcessAbove = (process: Process) => {
    // Get current processes for this cycle to determine index
    const currentProcesses = processesByCycleId[process.cycleId] || [];
    const processIndex = currentProcesses.findIndex(p => p.id === process.id);

    setCurrentCycleId(process.cycleId);
    setProcessDrawerMode('add');
    setSelectedProcessForEdit(null);
    setInsertProcessAtIndex(processIndex); // Insert at same index (pushes current down)
    setNextProcessCode(getNextProcessCode(process.cycleId, processIndex));
    setIsProcessDrawerOpen(true);
  };

  const handleAddProcessBelow = (process: Process) => {
    // Get current processes for this cycle to determine index
    const currentProcesses = processesByCycleId[process.cycleId] || [];
    const processIndex = currentProcesses.findIndex(p => p.id === process.id);

    setCurrentCycleId(process.cycleId);
    setProcessDrawerMode('add');
    setSelectedProcessForEdit(null);
    setInsertProcessAtIndex(processIndex + 1); // Insert after current process
    setNextProcessCode(getNextProcessCode(process.cycleId, processIndex + 1));
    setIsProcessDrawerOpen(true);
  };

  const handleEditProcess = (process: Process, shouldAutoSearchOwner: boolean = false) => {
    setCurrentCycleId(process.cycleId);
    setProcessDrawerMode('edit');
    setSelectedProcessForEdit(process);
    setNextProcessCode('');
    setAutoEnterSearchMode(shouldAutoSearchOwner);
    setIsProcessDrawerOpen(true);
  };

  const handleProcessSubmit = async (formData: ProcessFormData) => {
    if (!currentCycleId) {
      message.error('No cycle selected');
      throw new Error('No cycle selected');
    }

    try {
      if (processDrawerMode === 'add') {
        // Get current processes to determine index
        const currentProcesses = processesByCycleId[currentCycleId] || [];
        const index = insertProcessAtIndex !== null ? insertProcessAtIndex : currentProcesses.length;
        await createProcess({
          cycleId: currentCycleId,
          processName: formData.processName,
          processWeight: formData.processWeight,
          processOwnerId: formData.processOwner?.userId, // Optional
          index: index + 1,
          documentIds: formData.documents.map(d => d.id), // File IDs from upload
          testSheetId: formData.testSheet?.id, // File ID from upload
        });
        // Reset insert position after successful add
        setInsertProcessAtIndex(null);


      } else {
        // console.log('Updating process:', formData);
        await updateProcess({
          cycleId: currentCycleId,
          processId: formData.id || '',
          processName: formData.processName,
          processWeight: formData.processWeight,
          processOwnerId: formData.processOwner?.userId, // Optional
          documentIds: formData.documents.map(d => d.id), // File IDs from upload
          testSheetId: formData.testSheet?.id, // File ID from upload
        });

      }
      // Refresh processes list for this cycle
      try {
        await fetchAndUpdateProcesses(currentCycleId, dispatch);
      } catch (error) {
        console.error('Failed to refresh processes after update:', error);
        // Don't show error message, process was updated successfully
      }

      // Invalidate financial statement cache so it refetches on next visit
      // (Processes linked to financial statement items may have been re-indexed)
      dispatch(clearFinancialData());

      // Clear risk assessment cache when process is created (1:1 mapping with RiskAssessment)
      dispatch(clearRiskAssessmentData());

      // Clear certification cache (process owner assignment may have changed)
      dispatch(clearCertificationProcesses());

      // Clear gaps cache (processes contain gaps which map 1:1 to Gaps)
      dispatch(clearGapsData());

      message.success(`Process ${processDrawerMode === 'add' ? 'created' : 'updated'} successfully`);
    } catch (error) {
      const defaultErrorMessage = `Failed to ${processDrawerMode === 'add' ? 'create' : 'update'} process`;
      message.error(error instanceof Error ? error.message : defaultErrorMessage);
      throw error;
    }
  };

  const handleDeleteProcess = (process: Process) => {
    setProcessToDelete(process);
    setShowDeleteProcessModal(true);
  };

  const handleDeleteProcessConfirm = async () => {
    if (!processToDelete || !selectedReportId) {
      message.error('Cannot delete process');
      return;
    }

    const cycleIdToRefresh = processToDelete.cycleId; // Use cycleId from processToDelete
    setIsDeletingProcess(true);
    try {
      await deleteProcess(processToDelete.id);
      message.success('Process deleted successfully');
      // Close modal
      setShowDeleteProcessModal(false);
      setProcessToDelete(null);

      // Invalidate financial statement cache so it refetches on next visit
      // (Processes linked to financial statement items may have been removed)
      dispatch(clearFinancialData());

      // Clear risk assessment cache (1:1 mapping with Process)
      dispatch(clearRiskAssessmentData());

      // Clear certification cache (process owner assignments may have changed)
      dispatch(clearCertificationProcesses());

      // Clear gaps cache (processes contain gaps which map 1:1 to Gaps)
      dispatch(clearGapsData());

      // Refresh processes list for this cycle
      try {
        await fetchAndUpdateProcesses(cycleIdToRefresh, dispatch);
        // console.log(`✅ Refreshed processes for cycle after deletion`);
      } catch (error) {
        console.error('Failed to refresh processes after deletion:', error);
        message.warning('Process deleted but failed to refresh list. Please refresh the page.');
      }
    } catch (error) {
      console.error('Failed to delete process:', error);
      message.error('Failed to delete process');
    } finally {
      setIsDeletingProcess(false);
    }
  };

  const handleDeleteProcessCancel = () => {
    setShowDeleteProcessModal(false);
    setProcessToDelete(null);
  };

  const handleProcessDrawerClose = () => {
    setIsProcessDrawerOpen(false);
    setSelectedProcessForEdit(null);
    setCurrentCycleId('');
    setInsertProcessAtIndex(null); // Reset insert position
    setAutoEnterSearchMode(false); // Reset auto search mode flag
  };

  const handleRiskRankingClick = async (process: Process) => {
    navigate(`/risk-assessment/${process.riskAssessmentId}/tab1`);
  };

  const handleTotalControlsClick = (process: Process & { cycleId?: string }) => {
    navigate('/control-activities', { state: { cycleId: process.cycleId, processId: process.id } });
  };

  const handleKeyControlsClick = (process: Process & { cycleId?: string }) => {
    navigate('/control-activities', { state: { cycleId: process.cycleId, processId: process.id, isKey: true } });
  };

  const handleNavigateToGaps = (processId?: string, cycleId?: string, riskRating?: string) => {
    navigate('/gaps', { state: { processId, cycleId, riskRating } });
  };

  const handleControlWeightClick = (process: Process) => {
    navigate('/control-activities/manage-weights', {
      state: { cycleId: process.cycleId, processId: process.id },
    });
  };

  // Export columns configuration
  const exportColumns: ExportColumnConfig[] = useMemo(() => [
    { key: 'cycle', title: 'Cycle', width: 20 },
    { key: 'processCode', title: 'Process Code', width: 12 },
    { key: 'processName', title: 'Process Name', width: 25 },
    { key: 'processOwner', title: 'Process Owner', width: 18 },
    { key: 'riskRanking', title: 'Risk Ranking', width: 12 },
    { key: 'totalControls', title: 'Controls', width: 12 },
    { key: 'keyControls', title: 'Key Controls', width: 12 },
    { key: 'processWeight', title: 'Process Weight', width: 12 },
    { key: 'controlWeight', title: 'Control Weight', width: 12 },
    { key: 'testingStatus', title: 'Testing Status', width: 12 },
    { key: 'totalGaps', title: 'Total Gaps', width: 10 },
  ], []);

  // Prepare export data - flatten cycles and processes
  const exportData = useMemo(() => {
    const data: Record<string, unknown>[] = [];

    cycles.forEach(cycle => {
      const cycleProcesses = processesByCycleId[cycle.id] || [];
      cycleProcesses.forEach(process => {
        data.push({
          cycle: `${cycle.cycleCode} ${cycle.cycleName}`,
          processCode: process.processCode,
          processName: process.processName,
          processOwner: process.processOwner || '',
          riskRanking: process.riskRanking,
          totalControls: process.totalControls,
          keyControls: process.keyControls,
          processWeight: process.processWeight.toFixed(0),
          controlWeight: controlWeightByProcessId[process.id]?.toFixed(0) || 'N/A',
          testingStatus: `${process.testingStatus.toFixed(0)}%`,
          totalGaps: process.gaps?.reduce((sum, gap) => sum + gap.count, 0) || 0,
        });
      });
    });

    return data;
  }, [cycles, processesByCycleId]);

  // Calculate overall totals for summary row
  const summaryTotals = useMemo(() => {
    let totalProcessCount = 0;
    let totalControlsCount = 0;
    let totalKeyControlsCount = 0;
    let totalTestingStatusSum = 0;
    let totalProcessWeight = 0;
    let totalTestingStatusWeighted = 0;
    let totalCycleWeight = 0;
    let totalGaps = 0;

    cycles.forEach(cycle => {
      const cycleProcesses = processesByCycleId[cycle.id] || [];
      totalProcessCount += cycleProcesses.length;
      totalTestingStatusSum = 0;
      totalProcessWeight = 0;
      cycleProcesses.forEach(process => {
        totalControlsCount += process.totalControls;
        totalKeyControlsCount += process.keyControls;
        totalGaps += process.totalGaps;

        if (process.inScopeControlCount != 0) {
          totalTestingStatusSum += process.testingStatus * process.processWeight;
          totalProcessWeight += process.processWeight;
        } 
      });

      totalTestingStatusWeighted += totalProcessWeight !== 0 ? (totalTestingStatusSum / totalProcessWeight) * cycle.cycleWeight : 0;
      totalCycleWeight += cycle.cycleWeight;
    });

    const avgTestingStatus = totalProcessCount > 0 && totalCycleWeight !== 0
      ? Number((totalTestingStatusWeighted / totalCycleWeight).toFixed(0))
      : 0;

    return { totalProcessCount, totalControlsCount, totalKeyControlsCount, avgTestingStatus, totalGaps, totalCycleWeight };
  }, [cycles, processesByCycleId]);

  // Render loading state (prioritize over empty state on initial load)
  if (isLoadingCycles) {
    return (
      <div>
        <PageHeader>
          <PageTitle level={2}>ICFR Hierarchy</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading cycles...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </div>
    );
  }

  // Render empty state for entire page when no cycles
  // Only show if we've attempted to fetch (not initial load)
  if (cycles.length === 0 && lastFetchedReportId !== null) {
    return (
      <div>
        <PageHeader>
          <PageTitle level={2}>ICFR Hierarchy</PageTitle>
        </PageHeader>

        <PageEmptyStateWrapper>
          <PageEmptyStateText>No cycles have been created yet.</PageEmptyStateText>
          <AddItemButton onClick={handleAddCycle} text="Add Cycle" disabled={!isAdmin} />
        </PageEmptyStateWrapper>

        {/* Cycle Drawer - needed for adding first cycle */}
        <CycleItemDrawer
          open={isCycleDrawerOpen}
          mode={cycleDrawerMode}
          initialValues={selectedCycleForEdit || undefined}
          nextCycleCode={cycleDrawerMode === 'add' ? getNextCycleCode() : undefined}
          onClose={handleCycleDrawerClose}
          onSubmit={handleCycleSubmit}
        />
      </div>
    );
  }

  // Show nothing while waiting for initial load (before first fetch)
  if (cycles.length === 0 && lastFetchedReportId === null) {
    return null;
  }

  return (
    <div>
      <PageHeader>
        <PageHeaderRow>
          <PageTitle level={2}>ICFR Hierarchy</PageTitle>
          <HeaderActions>
            <AddItemButton onClick={handleAddCycle} text="Add Cycle" disabled={!isAdmin} />
            <ExportButton onClick={() => setExportPanelOpen(true)} />
            {selectedReport && isAdmin && (
              <ChangeLogButton
                actionType="hierarchy"
                annualReportId={selectedReport.annualReportId}
              />
            )}
          </HeaderActions>
        </PageHeaderRow>
        <SummaryRow>
          <SummaryItem>
            <SummaryLabel>Total Process Count:</SummaryLabel>
            <SummaryValue>{summaryTotals.totalProcessCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Controls Count:</SummaryLabel>
            <SummaryValue>{summaryTotals.totalControlsCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Key Control Count:</SummaryLabel>
            <SummaryValue>{summaryTotals.totalKeyControlsCount}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Weighted Avg Testing Status:</SummaryLabel>
            <SummaryValue>{summaryTotals.avgTestingStatus}%</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total GAPs:</SummaryLabel>
            <SummaryValue>{summaryTotals.totalGaps}</SummaryValue>
          </SummaryItem>
        </SummaryRow>
        {isAdmin && cycles.length > 0 && Math.abs(summaryTotals.totalCycleWeight - 100) >= 1 && ( // Update to whole number check, as no fractions are allowed anyways
          <ErrorBanner
            type="warning"
            showIcon
            message={`Cycle weights sum to ${summaryTotals.totalCycleWeight.toFixed(0)}% — they should add up to 100%. Please update your cycle weights.`}
          />
        )}
      </PageHeader>

      <ContentWrapper ref={contentWrapperRef}>
        {cycles.map((cycle, index) => (
          <CycleItem
            key={cycle.id}
            index={index}
            cycle={cycle}
            isCollapsed={collapsedCycles.has(cycle.id)}
            onToggleCollapse={() => toggleCycleCollapse(cycle.id)}
            onEditCycle={handleEditCycle}
            onDeleteCycle={handleDeleteCycle}
            onEditProcess={handleEditProcess}
            onDeleteProcess={handleDeleteProcess}
            onAddProcess={handleAddProcess}
            onAddProcessAbove={handleAddProcessAbove}
            onAddProcessBelow={handleAddProcessBelow}
            onRiskRankingClick={handleRiskRankingClick}
            onTotalControlsClick={handleTotalControlsClick}
            onKeyControlsClick={handleKeyControlsClick}
            onNavigateToGaps={handleNavigateToGaps}
            controlWeightByProcessId={controlWeightByProcessId}
            onControlWeightClick={handleControlWeightClick}
            cycleRef={(ref) => {
              cycleRefs.current[cycle.id] = ref;
            }}
          />
        ))}
      </ContentWrapper>

      {/* Cycle Drawer */}
      <CycleItemDrawer
        open={isCycleDrawerOpen}
        mode={cycleDrawerMode}
        initialValues={selectedCycleForEdit || undefined}
        nextCycleCode={cycleDrawerMode === 'add' ? getNextCycleCode() : undefined}
        onClose={handleCycleDrawerClose}
        onSubmit={handleCycleSubmit}
      />

      {/* Process Drawer */}
      <ProcessItemDrawer
        open={isProcessDrawerOpen}
        mode={processDrawerMode}
        initialValues={selectedProcessForEdit ? {
          id: selectedProcessForEdit.id,
          processCode: selectedProcessForEdit.processCode,
          processName: selectedProcessForEdit.processName,
          processWeight: selectedProcessForEdit.processWeight,
          riskRanking: selectedProcessForEdit.riskRanking,
          processOwner: selectedProcessForEdit.processOwner ? {
            userId: selectedProcessForEdit.processOwnerId,
            userName: selectedProcessForEdit.processOwner,
            title: '', // TODO: Get from backend when available
          } : null,
          // Map flowcharts and test sheets  file IDs
          documents: selectedProcessForEdit.flowcharts,
          testSheet: selectedProcessForEdit.testSheet,
          totalGaps: selectedProcessForEdit.gaps?.reduce((sum, gap) => sum + gap.count, 0) || 0,
        } : undefined}
        nextProcessCode={nextProcessCode}
        autoEnterSearchMode={autoEnterSearchMode}
        onClose={handleProcessDrawerClose}
        onSubmit={handleProcessSubmit}
      />

      {/* Delete Cycle Confirmation Modal */}
      <ConfirmModal
        open={showDeleteCycleModal}
        onClose={handleDeleteCycleCancel}
        onConfirm={handleDeleteCycleConfirm}
        icon={warningIcon}
        title="Delete Cycle"
        description={
          cycleToDelete ? (
            <div style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: '8px' }}>
                Are you sure you want to delete <strong>{cycleToDelete.cycleCode} {cycleToDelete.cycleName}</strong>?. This action will:
              </div>
              <ul style={{ fontSize: '14px', marginBottom: '16px' }}>
                <li>Delete this cycle and all its associated processes</li>
                <li>Re-index all subsequent cycles and processes</li>
                <li>Update all downstream references automatically</li>
              </ul>
              <div style={{ color: '#DC2626', fontWeight: 600 }}>
                This action cannot be undone.
              </div>
            </div>
          ) : 'Are you sure you want to delete this cycle?'
        }
        confirmText="Delete Cycle"
        cancelText="Cancel"
        danger={true}
        loading={isDeletingCycle}
      />

      {/* Delete Process Confirmation Modal */}
      <ConfirmModal
        open={showDeleteProcessModal}
        onClose={handleDeleteProcessCancel}
        onConfirm={handleDeleteProcessConfirm}
        icon={warningIcon}
        title="Delete Process"
        description={'Are you sure you want to delete this process? This action cannot be undone.'}
        confirmText="Delete Process"
        cancelText="Cancel"
        danger={true}
        loading={isDeletingProcess}
      />

      {/* Export Panel */}
      <ExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
        data={exportData}
        columns={exportColumns}
        fileName="ProcessHierarchy"
        title="ICFR Hierarchy"
      />
    </div>
  );
};

export default ProcessHierarchyPage;
